import { ChangeDetectorRef, Component, Inject, PLATFORM_ID, ViewChild } from '@angular/core';
import { MachineListDto } from '../../../../core/models/machines/machine-list-dto';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MachineListFilterComponent } from '../../../../core/components/machine-list-filter/machine-list-filter.component';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MachineListService } from '../../../../core/services/machine-list/machine-list.service';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { Router, RouterModule } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MachineListFilter } from '../../../../core/models/machines/machine-list-filter';
import { allColumns } from './column-groups';
import { BehaviorSubject, map, Observable, Subject, takeUntil } from 'rxjs';
import { ColumnManagerService } from '../../../../core/services/column-manager/column-manager.service';
import { MatButtonModule } from '@angular/material/button';
import { ColumnDefinition, ColumnDefinitionMachineList, GenericCellType } from '../../../../core/models/column-definition';
import { MatDialog } from '@angular/material/dialog';
import { FilterItem } from '../../../../core/models/filter-item';
import { AuthService } from '../../../../core/services/auth.service';
import { MachineListTeamInfoDto } from '../../../../core/models/machines/machine-list-team-info-dto';
import * as XLSX from 'xlsx';
import { IconService } from '../../../../core/services/icon.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { SortCoordinationService } from '../../../../core/services/sort-coordination/sort-coordination.service';
import { MatMenuModule } from '@angular/material/menu';

@Component({
  selector: 'app-machine-list',
  imports: [MatTableModule, MatPaginatorModule, MatSortModule, RouterModule, CommonModule, MatIconModule, MachineListFilterComponent, MatButtonModule, FontAwesomeModule, MatMenuModule],
  templateUrl: './machine-list.component.html',
  styleUrl: './machine-list.component.scss'
})
export class MachineListComponent {
  private readonly ngUnsubscribe$ = new Subject<void>();
  private allColumns: ColumnDefinition[] = allColumns;

  private readonly tableId = 'machine-table';

  machines: MachineListDto[] = [];
  protected filteredMachineList$: Observable<MatTableDataSource<MachineListDto>> | undefined;
  machineDataSource = new MatTableDataSource<MachineListDto>;
  isLoading = true;
  displayedColumns: (string | undefined)[] = [];
  skeletonRows = Array(20).fill(0);

  data$!: BehaviorSubject<MachineListDto[] | null>;
  @ViewChild(MatPaginator) paginator: MatPaginator | undefined;
  @ViewChild(MatSort) sort: MatSort | undefined;

  combinedColumns: ColumnDefinitionMachineList[] = [];
  isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) private platformId: Object,
    private machineListService: MachineListService,
    private changeDetectorRef: ChangeDetectorRef,
    private columnManagerService: ColumnManagerService,
    private dialog: MatDialog,
    public authService: AuthService,
    private router: Router,
    public iconService: IconService,
    private sortCoordinationService: SortCoordinationService) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnDestroy() {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  private initSubscriptions = (): void => {
    this.filteredMachineList$ = this.machineListService.filterChange.pipe(takeUntil(this.ngUnsubscribe$))
      .pipe(
        map((value) => {
          return this.applyFilter(
            value || ""
          );
        })
      );
  }

  async ngOnInit(): Promise<void> {
    if (!this.isBrowser) {
      this.isLoading = false;
      return;
    }

    this.columnManagerService.registerTable(this.tableId, this.allColumns);

    this.columnManagerService.loadFromStorage(this.tableId);

    this.displayedColumns = this.columnManagerService.getDisplayedColumns(this.tableId);
    this.combinedColumns = this.allColumns.filter(cc => this.displayedColumns.includes(cc.columnDef)) as ColumnDefinitionMachineList[];

    this.machineListService.loadMachines();
    this.data$ = this.machineListService.data$;
    this.data$.subscribe(machines => {
      if (machines) {
        this.machines = machines;
        this.isLoading = false;
      }
    })
    this.initSubscriptions();

    this.sortCoordinationService.clearSort$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(tableId => {
        if (tableId === this.tableId && this.sort) {
          this.sort.active = '';
          this.sort.direction = '';
          this.columnManagerService.clearSortState(this.tableId);
          this.sort.sortChange.emit({ active: '', direction: '' });
        }
      });
  }

  async ngAfterViewInit(): Promise<void> {
    this.machineListService.filterChange.subscribe(filter => {
      this.applyFilter(filter);
      this.changeDetectorRef.detectChanges();
    });
    this.machineDataSource.paginator = this.paginator!;
    this.machineDataSource.sort = this.sort!;

    const savedPageSize = this.columnManagerService.loadPageSize(this.tableId);
    if (savedPageSize && this.paginator) {
      this.paginator.pageSize = savedPageSize;
    }

    this.paginator?.page.pipe(takeUntil(this.ngUnsubscribe$)).subscribe((pageEvent) => {
      this.columnManagerService.savePageSize(this.tableId, pageEvent.pageSize);
    });

    const savedSort = this.columnManagerService.loadSortState(this.tableId);
    if (savedSort && this.sort) {
      this.sort.active = savedSort.active;
      this.sort.direction = savedSort.direction;
      this.sort.sortChange.emit({ active: savedSort.active, direction: savedSort.direction });
    }

    this.sort?.sortChange.pipe(takeUntil(this.ngUnsubscribe$)).subscribe(() => {
      if (this.sort) {
        this.columnManagerService.saveSortState(this.tableId, this.sort);
      }
    });

    this.machineDataSource.sortingDataAccessor = (item: any, property: string): any => {
      if (property === 'baseInfo.teams.teamId') {
        return item.baseInfo.teams?.filter((t: MachineListTeamInfoDto) => t.active).map((team: MachineListTeamInfoDto) => team.teamId).join(', ') || '';
      }
      if (property === 'baseInfo.teams.name') {
        return item.baseInfo.teams?.filter((t: MachineListTeamInfoDto) => t.active).map((team: MachineListTeamInfoDto) => team.name).join(', ') || '';
      }

      return property.split('.').reduce((obj, key) => obj?.[key], item);
    };
  }

  openColumnManager() {
    this.columnManagerService.openColumnManager(this.tableId)
      .subscribe(result => {
        if (result) {

          this.displayedColumns = result.displayedColumns;
          this.combinedColumns = this.allColumns.filter(cc => this.displayedColumns.includes(cc.columnDef)) as ColumnDefinitionMachineList[];


          this.columnManagerService.saveToStorage(this.tableId);
        }
      });
  }

  applyFilter(filterValues: MachineListFilter) {
    if (!this.data$ || !this.data$.value) {
      return this.machineDataSource;
    }
    let machines = this.data$.value;

    if (!machines) {
      return this.machineDataSource;
    }

    if (filterValues?.productionTechnicianArea?.length) {
      machines = machines.filter((machine) => this.isListPropertyFiltered(filterValues.productionTechnicianArea, machine.baseInfo.productionTechnicianAreas));
    }

    if (filterValues?.productionLeader?.length) {
      machines = machines.filter((machine) => this.isListPropertyFiltered(filterValues.productionLeader, machine.baseInfo.productionLeaders));
    }
    if (filterValues?.machineType?.length) {
      machines = machines.filter((machine) => this.isPropertyFiltered(filterValues.machineType, machine.baseInfo.machineTypeText));
    }

    if (filterValues?.ownerType?.length) {
      machines = machines.filter((machine) => this.isPropertyFiltered(filterValues.ownerType, machine.baseInfo.ownerType));
    }

    if (filterValues?.hasHighPrecisionCraneTipCoordinate.length) {
      machines = machines.filter((machine) => this.isPropertyFiltered(filterValues.hasHighPrecisionCraneTipCoordinate, machine.equipmentInfo.hasHighPrecisionCraneTipCoordinateText));
    }

    if (filterValues?.hasRtk.length) {
      machines = machines.filter((machine) => this.isPropertyFiltered(filterValues.hasRtk, machine.equipmentInfo.hasRtkText));
    }

    if (filterValues?.status.length) {
      machines = machines.filter((machine) => this.isPropertyFiltered(filterValues.status, machine.baseInfo.machineStatusText));
    }

    if (filterValues?.isSchoolForwarder?.length) {
      machines = machines.filter((machine) => this.isPropertyFiltered(filterValues.isSchoolForwarder, machine.baseInfo.isSchoolForwarderText));
    }

    if (filterValues?.isGrotForwarder?.length) {
      machines = machines.filter((machine) => this.isPropertyFiltered(filterValues.isGrotForwarder, machine.baseInfo.isGrotForwarderText));
    }

    if (filterValues?.isDemoMachine?.length) {
      machines = machines.filter((machine) => this.isPropertyFiltered(filterValues.isDemoMachine, machine.baseInfo.isDemoMachineText));
    }

    const activeSearch = filterValues?.search?.find((item) => item?.activated && !!item?.value?.trim());
    if (activeSearch) {
      machines = machines.filter((machine) => this.checkNestedObject(machine, activeSearch.value, activeSearch.activated));
    }

    this.machineDataSource.data = machines;

    if (this.paginator) this.machineDataSource.paginator = this.paginator;
    if (this.sort) this.machineDataSource.sort = this.sort;

    return this.machineDataSource;
  }

  isPropertyFiltered(filterItems: FilterItem[], property: string): boolean {
    return filterItems.every(f => !f.activated) || filterItems.some(f => f.value === property && f.activated);
  }

  isListPropertyFiltered(filterItems: FilterItem[], properties: string[]): boolean {
    return filterItems.every(f => !f.activated) || filterItems.some(f => properties.some(p => f.value === p && f.activated));
  }

  checkNestedObject = (obj: any, filter: string, activated: boolean = true): boolean => {
    if (!activated) {
      return true;
    }

    if (!filter) {
      return true;
    }

    return Object.keys(obj).some(key => {
      const value = obj[key];
      if (value && typeof value === 'object') {
        return this.checkNestedObject(value, filter);
      }
      const lowerCaseFilter = filter.toLowerCase();
      return value && value.toString().toLowerCase().includes(lowerCaseFilter);
    });
  };

  getProperty = (obj: any, path: any) => (
    path.split('.').reduce((o: any, p: any) => o && o[p], obj)
  )

  getCellValue(value: GenericCellType): GenericCellType {
    const returnedValue = (value !== 'null' && value !== 'undefined') ? value : '-';
    return returnedValue;
  }

  getArrayCellValue(cellValue: any, index: number): string {
    if (Array.isArray(cellValue) && cellValue[index] !== undefined) {
      return String(cellValue[index]);
    }
    return '';
  }

  openCreateMachineDialog() {

    const machineId = 0;
    this.router.navigate(['/newmachine/' + machineId], { queryParams: { type: 'new' } });
  }

  exportToExcel() {
    let element = document.getElementById('machine-list');
    const ws: XLSX.WorkSheet = XLSX.utils.table_to_sheet(element, { raw: true });

    ws['!cols'] = [{ wch: 10 }, { wch: 15 }, { wch: 30 }, { wch: 10 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 20 }, { wch: 10 }, { wch: 15 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }];
    ws['A1'].v = 'Maskinnr';

    // Formatera alla celler som text f�r att f�rhindra datumtolkning
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    for (let row = range.s.r; row <= range.e.r; row++) {
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        const cell = ws[cellAddress];
        if (cell) {
          cell.t = 's';
          cell.z = '@';
        }
      }
    }

    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Maskiner');

    XLSX.writeFile(wb, 'Maskiner.xlsx');
  }
}
